import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CommentIcon from '@mui/icons-material/Comment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import { formatDateTime, formatDateTimeLong, getFileType } from './patrolUtils';
import InlineChecklistDetail from './InlineChecklistDetail';

const PatrolExpandedRow = ({ patrolData, readOnly, onRefetch, onViewFile }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        my: 1.5,
        mx: 1,
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.grey[500], 0.08),
        boxShadow: `inset 0 1px 3px ${alpha(theme.palette.grey[500], 0.1)}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PersonIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="body2">
            <strong>{patrolData?.guardName}</strong>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
          <Typography variant="body2" color="text.secondary">
            {formatDateTimeLong(patrolData?.startTime)} - {formatDateTime(patrolData?.endTime).split(', ')[1]}
          </Typography>
        </Box>
        <Chip
          label={patrolData?.patrolType === 'Static' ? 'Static' : 'Mobile'}
          size="small"
          variant="outlined"
          sx={{ height: 22, fontSize: 11 }}
        />
      </Box>

      {/* Comment */}
      {patrolData?.comment && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <CommentIcon sx={{ fontSize: 16, mt: 0.25, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{patrolData.comment}</span>
          </Typography>
        </Box>
      )}

      {/* Attachments */}
      {patrolData?.attachments && patrolData.attachments.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <AttachFileIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            <Typography variant="body2" fontWeight={500}>
              Attachments ({patrolData.attachments.length})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, pl: 2.5 }}>
            {patrolData.attachments.map((file) => {
              const fileType = getFileType(file);
              const isViewable = fileType === 'image' || fileType === 'video';
              const FileIcon = fileType === 'video' ? VideocamIcon : ImageIcon;
              return (
                <Tooltip key={file.id} title={isViewable ? 'Click to view' : file.name}>
                  <Chip
                    label={file.name}
                    size="small"
                    icon={<FileIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isViewable) onViewFile(file);
                    }}
                    sx={{
                      height: 24,
                      fontSize: 11,
                      cursor: isViewable ? 'pointer' : 'default',
                      '&:hover': isViewable ? {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      } : {},
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Checklists */}
      <InlineChecklistDetail
        scheduleAssignmentId={patrolData?.scheduleAssignmentId}
        allowEdit={!readOnly}
        onRefetch={onRefetch}
      />
    </Box>
  );
};

export default PatrolExpandedRow;
